import * as React from "react"
import { Head, Link, usePage } from "@inertiajs/react"
import { ChevronDown, ChevronRight, Plus } from "lucide-react"
import { AppShell } from "@/components/AppShell"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { TaskFormDialog } from "@/components/tasks/TaskFormDialog"
import { TaskList } from "@/components/tasks/TaskList"
import { cn } from "@/lib/utils"
import type { TaskRecord } from "@/components/tasks/types"
import type { PageProps } from "@/types/inertia"

type ClientGroup = {
  readonly client: { readonly id: number; readonly display_name: string; readonly color_hex: string } | null
  readonly tasks: readonly TaskRecord[]
}

type TasksProps = {
  readonly today: {
    readonly overdue: readonly TaskRecord[]
    readonly due_today: readonly TaskRecord[]
  }
  readonly by_client: readonly ClientGroup[]
  readonly completed_recent: readonly TaskRecord[]
}

export default function TasksPage({ today, by_client, completed_recent }: TasksProps) {
  const { props } = usePage<PageProps<TasksProps>>()
  const description = "Your tasks: overdue, due today, grouped by client, and recently completed."

  return (
    <>
      <Head title="Tasks">
        <meta name="description" content={description} />
        <meta property="og:title" content="Tasks" />
        <meta property="og:description" content={description} />
      </Head>
      <AppShell wide>
        <div className="flex flex-col gap-8">
          <header className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1>Tasks</h1>
              <p className="mt-2">Overdue and upcoming tasks, grouped by client.</p>
            </div>
            <TaskFormDialog
              trigger={
                <Button type="button">
                  <Plus className="h-4 w-4" />
                  New task
                </Button>
              }
            />
          </header>

          {props.flash.notice && (
            <div className="rounded-md border border-accent bg-accent-faded px-4 py-3 text-sm text-accent-display">
              {props.flash.notice}
            </div>
          )}

          <section className="flex flex-col gap-4">
            <h2>Today</h2>
            <div className="flex flex-col gap-6">
              <div>
                <h3 className="text-danger-display">Overdue</h3>
                <TodayTaskList tasks={today.overdue} emptyText="No overdue tasks." overdue />
              </div>
              <div>
                <h3>Due today</h3>
                <TodayTaskList tasks={today.due_today} emptyText="Nothing due today." />
              </div>
            </div>
          </section>

          <section className="flex flex-col gap-4">
            <h2>By client</h2>
            {by_client.length === 0 ? (
              <p>No open tasks.</p>
            ) : (
              <div className="flex flex-col gap-4">
                {by_client.map((group) => (
                  <ClientTaskGroup key={group.client?.id ?? "unassigned"} group={group} />
                ))}
              </div>
            )}
          </section>

          {completed_recent.length > 0 && (
            <section className="flex flex-col gap-4">
              <CollapsibleSection title="Recently completed" defaultOpen={false}>
                <TaskList tasks={completed_recent} emptyText="No recently completed tasks." />
              </CollapsibleSection>
            </section>
          )}
        </div>
      </AppShell>
    </>
  )
}

function TodayTaskList({
  tasks,
  emptyText,
  overdue,
}: {
  readonly tasks: readonly TaskRecord[]
  readonly emptyText: string
  readonly overdue?: boolean
}) {
  return (
    <div className={cn("overflow-hidden rounded-lg border border-hairline bg-page", overdue && "border-amber-600/30")}>
      {tasks.length === 0 ? (
        <p className="px-4 py-3 text-sm text-ink-muted">{emptyText}</p>
      ) : (
        <ul className="divide-y divide-hairline">
          {tasks.map((task) => (
            <TaskRow key={task.id} task={task} overdue={overdue} />
          ))}
        </ul>
      )}
    </div>
  )
}

function TaskRow({
  task,
  overdue,
}: {
  readonly task: TaskRecord
  readonly overdue?: boolean
}) {
  return (
    <li className="flex items-start gap-3 px-4 py-3">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="truncate">{task.title}</span>
          {task.source === "auto_followup" && (
            <Badge tone="muted">auto</Badge>
          )}
        </div>
        {task.due_on && (
          <div className={cn("mt-1 text-sm", overdue ? "text-danger-display font-medium" : "text-ink-muted")}>
            Due {formatDate(task.due_on)}
          </div>
        )}
        {task.notes && (
          <p className="mt-1 text-sm text-ink-muted truncate">{task.notes}</p>
        )}
        {task.opportunity && (
          <Link
            href={`/crm/opportunities/${task.opportunity.id}`}
            className="mt-1 inline-block text-sm text-accent no-underline hover:underline"
          >
            {task.opportunity.name}
          </Link>
        )}
      </div>
      <div className="flex shrink-0 gap-1">
        <TaskFormDialog
          task={task}
          trigger={
            <Button type="button" variant="ghost" size="icon" aria-label="Edit task">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M11.5 1.5l3 3L5 14H2v-3L11.5 1.5z" />
              </svg>
            </Button>
          }
        />
      </div>
    </li>
  )
}

function ClientTaskGroup({ group }: { readonly group: ClientGroup }) {
  const [open, setOpen] = React.useState(true)
  const icon = open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />

  return (
    <div className="overflow-hidden rounded-lg border border-hairline bg-page">
      <button
        type="button"
        className="flex w-full items-center gap-2 px-4 py-3 text-left hover:bg-surface cursor-pointer"
        onClick={() => setOpen(!open)}
      >
        {icon}
        <span className="font-medium">
          {group.client ? (
            <span className="inline-flex items-center gap-2">
              <span
                className="h-2.5 w-2.5 rounded-full border border-hairline shrink-0"
                style={{ backgroundColor: group.client.color_hex }}
              />
              {group.client.display_name}
            </span>
          ) : (
            "Unassigned"
          )}
        </span>
        <Badge tone="muted">{group.tasks.length}</Badge>
      </button>
      {open && (
        <div className="border-t border-hairline">
          <TaskList tasks={group.tasks} emptyText="No tasks for this client." />
        </div>
      )}
    </div>
  )
}

function CollapsibleSection({
  title,
  defaultOpen,
  children,
}: {
  readonly title: string
  readonly defaultOpen: boolean
  readonly children: React.ReactNode
}) {
  const [open, setOpen] = React.useState(defaultOpen)
  const icon = open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />

  return (
    <div>
      <button
        type="button"
        className="flex items-center gap-2 cursor-pointer"
        onClick={() => setOpen(!open)}
      >
        {icon}
        <h2>{title}</h2>
      </button>
      {open && <div className="mt-3">{children}</div>}
    </div>
  )
}

function formatDate(value: string) {
  return new Date(value).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  })
}
